import { useSnapshot } from 'valtio';

import { saleorAuthClient } from '@/graphql/client';
import type { CountryCode } from '@/graphql/saleor/api';
import { EVENT_NAME } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import type { WishlistState } from '@/states/wishlistState';
import { wishListState } from '@/states/wishlistState';
import type { SkuWishlistMap, WishlistItem } from '@/views/WishListView/types';
import {
  addItemsToServerWishlistByCountry,
  getLocalWishlistByCountry,
  getServerWishlistByCountry,
  updateLocalWishlistByCountry,
} from '@/views/WishListView/utils';

import { useGoogleAnalytics } from './useGoogleAnalytics';

export interface WishlistProductProps {
  price: number | string | undefined;
  sku: string | null | undefined;
  theCurrency: string | undefined;
  type: string;
}
export interface ProductFavoriteProps {
  country?: CountryCode;
  price?: number | undefined;
  sku?: string | null | undefined;
  theCurrency?: string | undefined;
  type?: string;
  wishlist?: WishlistProductProps[];
}

export const useWishList = ({ countryCode }: { countryCode: CountryCode }) => {
  const { sendEcommerceEventBySkus } = useGoogleAnalytics();
  const snapshot = useSnapshot<WishlistState>(wishListState);
  const { isHighlighted } = snapshot;

  const getWishList = async () => {
    const localWishlistMap = getLocalWishlistByCountry(countryCode);
    const serverWishlist = await getServerWishlistByCountry(countryCode);

    const sortedLocalWishlist = Object.entries(localWishlistMap)
      .map(([sku, item]) => ({ ...item, sku }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    const filteredLocalWishlist = sortedLocalWishlist.filter(
      (item) =>
        !serverWishlist.some((serverItem) => serverItem.sku === item.sku),
    );

    const wishlist = [...serverWishlist, ...filteredLocalWishlist];

    const wishlistMap = wishlist.reduce((acc, item) => {
      if (item.sku) {
        const localItem = sortedLocalWishlist.find(
          (localItem) => localItem.sku === item.sku,
        );

        acc[item.sku] = { ...item, createdAt: localItem?.createdAt };
      }
      return acc;
    }, {} as SkuWishlistMap);

    updateLocalWishlistByCountry(countryCode, wishlistMap);

    return { localWishlistMap, serverWishlist, wishlist };
  };

  const getWishListForAll = async () => {
    const { wishlist } = await getWishList();

    return wishlist;
  };

  /**
   * Combine the customer's wishlist for the current country from local and the server,
   * then sync the merged wishlist to both local and the server.
   */
  const mergeWishlist = async () => {
    const { localWishlistMap, serverWishlist } = await getWishList();

    const serverWishlistMap = serverWishlist.reduce((acc, item) => {
      if (item.sku) {
        acc[item.sku] = item;
      }
      return acc;
    }, {} as SkuWishlistMap);

    const serverMissingItems: SkuWishlistMap = {};
    Object.keys(localWishlistMap).forEach((sku) => {
      if (!serverWishlistMap[sku]) {
        serverMissingItems[sku] = localWishlistMap[sku];
      }
    });

    if (Object.keys(serverMissingItems).length > 0) {
      await addItemsToServerWishlistByCountry(countryCode, serverMissingItems);
    }
  };

  /**
   * Check if the product item exists in local wishlist of current country
   * @param sku
   */
  const isItemInLocalWishlist = (sku: string) => {
    const localWishlist = getLocalWishlistByCountry(countryCode);
    return !!(sku && sku in localWishlist);
  };

  const addItemToServerWishlist = async (item: WishlistItem) => {
    return await addItemsToServerWishlistByCountry(countryCode, {
      [item.sku]: item,
    });
  };

  const addItemToLocalWishlist = (item: WishlistItem) => {
    const sku = item.sku;
    const prevLocalWishlist = getLocalWishlistByCountry(countryCode);
    if (!isItemInLocalWishlist(sku)) {
      const newLocalWishlist = {
        ...prevLocalWishlist,
        [sku]: { ...item, createdAt: Date.now() },
      };

      updateLocalWishlistByCountry(countryCode, newLocalWishlist);

      if (sku) {
        // Send rich ecommerce event to GA (includes product data from Algolia)
        sendEcommerceEventBySkus({
          event: EVENT_NAME.ADD_TO_WISHLIST,
          skus: [sku],
        });
      }
    }
  };

  const removeItemFromServerWishlist = async (sku?: string) => {
    try {
      if (sku) {
        await saleorAuthClient
          .fetchWithAuth(
            `${paths.api.wishlist}/${countryCode}/${sku}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              cache: 'no-store',
            },
            { allowPassingTokenToThirdPartyDomains: true },
          )
          .then((res) => res.json());
      }
    } catch (error) {
      //TODO add error toast
    }
  };

  const removeItemFromLocalWishlist = (sku: string) => {
    const localWishlist = getLocalWishlistByCountry(countryCode);
    delete localWishlist[sku];
    updateLocalWishlistByCountry(countryCode, localWishlist);
  };
  return {
    isHighlighted,
    getWishListForAll,
    isItemInLocalWishlist,
    addItemToLocalWishlist,
    removeItemFromLocalWishlist,
    addItemToServerWishlist,
    removeItemFromServerWishlist,
    mergeWishlist,
  };
};
